/**
 * Application entry point.
 *
 * Order matters: the runtime configuration is fetched first, because it decides which API the
 * pages talk to (the backend, or the demo backend of the `standalone` profile), and that has to
 * be settled before the first page loads anything.
 */
import { loadConfig } from './lib/config/manager.js';
import { useApi } from './app/api/index.js';
import { httpApi } from './app/api/plane-api.js';
import { createDemoApi } from './app/demo/demo-api.js';

import './app/register-components.js';

function failed(error) {
  console.error('Failed to start the application:', error);
  const message = document.createElement('p');
  message.style.padding = '2rem';
  message.textContent = `The application could not start: ${error.message}`;
  document.body.replaceChildren(message);
}

async function start() {
  const config = await loadConfig();
  useApi(config.api?.backend === 'demo' ? createDemoApi() : httpApi);
  document.body.replaceChildren(document.createElement('plane-app'));
  registerServiceWorker(config);
}

/**
 * Registration is driven by `pwa.serviceWorker`, not by the production flag: whether the
 * application wants a worker is a decision of its own, reversible without a code change.
 *
 * With the flag off the function does the opposite job: it removes any worker an earlier build
 * left on the device. A registered service worker outlives the deploy that introduced it, so
 * turning the feature off has to be an action, not just the absence of one.
 *
 * The scope is "/", the whole application: the worker itself lets through what is not the
 * application's (Dash under "/-/", the API, the clawlet endpoints).
 */
function registerServiceWorker(config) {
  if (!('serviceWorker' in navigator)) {
    return;
  }

  if (!config.pwa?.serviceWorker) {
    navigator.serviceWorker
      .getRegistrations()
      .then((registrations) => registrations.forEach((registration) => registration.unregister()))
      .catch((error) => console.error('Unable to remove a previously registered service worker:', error));
    return;
  }

  navigator.serviceWorker
    .register('/sw.js', { scope: '/', updateViaCache: 'none' })
    .then((registration) => {
      // Ask for an update now and whenever the tab comes back to the foreground: an installed
      // plane can stay open for days, and the browser's own heuristics are not to be relied on.
      registration.update().catch(() => {});
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') {
          registration.update().catch(() => {});
        }
      });
      watchForUpdate(registration);
    })
    .catch((error) => console.error('Service worker registration failed:', error));
}

/**
 * Turns "a new worker is waiting" into the user's own decision, never an automatic reload: a
 * revision being edited would be lost. `installed` alone is not enough of a signal: it also fires
 * on the very first install, when there is nothing to update from.
 */
function watchForUpdate(registration) {
  // A worker already waiting when the page loaded (an update found by another tab, or by a
  // previous visit that was closed before the prompt was answered).
  if (registration.waiting && navigator.serviceWorker.controller) {
    showUpdatePrompt(registration);
  }

  registration.addEventListener('updatefound', () => {
    const newWorker = registration.installing;
    if (!newWorker) {
      return;
    }
    newWorker.addEventListener('statechange', () => {
      if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
        showUpdatePrompt(registration);
      }
    });
  });

  // The waiting worker takes over only when the user asks for it (below); this is that takeover.
  // It takes over every open tab at once, but only the tab where the user said "Update" reloads:
  // another one may hold a revision being edited, and is asked instead. Guarded so that a second
  // controllerchange cannot reload twice.
  let reloaded = false;
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (reloaded) {
      return;
    }
    if (!updateAccepted) {
      showUpdatePrompt(registration);
      return;
    }
    reloaded = true;
    window.location.reload();
  });
}

// Set in the tab where the user accepted the update: the one tab allowed to reload on its own.
let updateAccepted = false;

function showUpdatePrompt(registration) {
  let prompt = document.querySelector('plane-update-prompt');
  if (!prompt) {
    prompt = document.createElement('plane-update-prompt');
    prompt.addEventListener('update-prompt/accepted', () => {
      updateAccepted = true;
      if (registration.waiting) {
        registration.waiting.postMessage('skipWaiting');
      } else {
        // Already activated from another tab: nothing to wait for, this tab only has to reload.
        window.location.reload();
      }
    });
    document.body.append(prompt);
  }
  prompt.show();
}

start().catch(failed);
